// src/features/doctor/DoctorSignatureCaptureCard.jsx
import React from "react";
import Card from "@/components/ui/Card.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import SaveChip from "@/sot/primitives/SaveChip.jsx";

const SIGNATURE_BUCKET = "doctor-assets";
const SIGNATURE_MIME = "image/png";

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function dataUrlToBlob(dataUrl) {
  const [meta, b64] = String(dataUrl).split(",");
  const mime = /data:(.*?);base64/.exec(meta)?.[1] || SIGNATURE_MIME;
  const bin = atob(b64 || "");
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

async function blobFromCanvas(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Failed to create image blob."))),
      SIGNATURE_MIME,
      1
    );
  });
}

/**
 * DoctorSignatureCaptureCard
 *
 * Props:
 * - value: string (dataURL or URL) optional
 * - onChange(nextSrc): called when signature changes (draw/upload/camera)
 * - persistToSupabase: boolean (default true). If false, Save only calls onChange + shows "Saved" UI without DB.
 * - title: string (default "Signature")
 * - subtitle: string
 * - showHeader: boolean (default true)
 * - hidePreview: boolean (default false)
 * - embedded: boolean (default false). Removes outer card chrome so the content can sit inside another surface cleanly.
 */
export default function DoctorSignatureCaptureCard({
  value = "",
  onChange,
  onSaved,
  persistToSupabase = true,
  title = "",
  subtitle = "",
  showHeader = true,
  hidePreview = false,
  embedded = false,
}) {
  const { t } = useLanguage();
  const canvasRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const uploadInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [ok, setOk] = React.useState("");
  const [hasInk, setHasInk] = React.useState(false);
  const [userId, setUserId] = React.useState(null);
  const [sessionState, setSessionState] = React.useState({ ok: true, message: "" });

  const [sigPath, setSigPath] = React.useState(null);
  const [sigPreviewUrl, setSigPreviewUrl] = React.useState("");
  const [localSrc, setLocalSrc] = React.useState(""); // draw/upload result (dataURL)

  const drawingRef = React.useRef({ drawing: false, lastX: 0, lastY: 0 });

  const effectiveSrc = localSrc || value || sigPreviewUrl || "";
  const resolvedTitle = title || t("common.signature.title", "Signature");
  const resolvedSubtitle =
    subtitle ||
    t(
      "common.signature.subtitle",
      "Sign once. AfriMed™ will snapshot this signature into each issued prescription.",
    );

// Something “saveable” exists if:
// - user uploaded/loaded a dataURL into localSrc, OR
// - they drew ink on the canvas, OR
// - the incoming `value` already contains a dataURL (edit existing signature)
  const hasValueDataUrl = Boolean(value && String(value).startsWith("data:image"));
  const canSaveNow = Boolean(localSrc || (hasInk && canvasRef.current) || hasValueDataUrl);

  // Save disabled only when there is nothing to save or we are in-flight.
  // Auth is verified inside saveSignature to avoid dead-locking the button on slow session hydration.
  const disabled = Boolean(
    saving || !canSaveNow
  );

  // ---------- canvas sizing ----------
  const resizeCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const rect = wrap.getBoundingClientRect();
    const cssW = Math.max(280, Math.floor(rect.width));
    const cssH = 180;

    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "rgb(2,6,23)";
  }, []);

  React.useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [resizeCanvas]);

  // ---------- load existing signature path (only when persisting) ----------
  React.useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr("");
      setOk("");

      try {
        if (!persistToSupabase) {
          if (!alive) return;
          setLoading(false);
          setUserId("local");
          return;
        }

        const { data: auth } = await supabase.auth.getUser();
        const user = auth?.user;
        if (!user) {
          if (!alive) return;
          setUserId(null);
          setSessionState({
            ok: false,
            message: t(
              "common.signature.signInToAdd",
              "Please sign in to add your signature.",
            ),
          });
          setLoading(false);
          return;
        }

        setUserId(user.id);
        setSessionState({ ok: true, message: "" });

        const { data: dp, error: dpErr } = await supabase
          .from("doctor_profiles")
          .select("signature_path, signature_mime, signature_updated_at")
          .eq("user_id", user.id)
          .maybeSingle();

        if (dpErr) throw dpErr;

        const path = dp?.signature_path || null;
        if (!alive) return;

        setSigPath(path);

        if (path) {
          const { data: signed, error: sErr } = await supabase.storage
            .from(SIGNATURE_BUCKET)
            .createSignedUrl(path, 60 * 10);

          if (!sErr && signed?.signedUrl) setSigPreviewUrl(signed.signedUrl);
        }
      } catch (e) {
        if (!alive) return;
        setErr(
          e?.message ||
            t("common.signature.loadFailed", "Failed to load signature."),
        );
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [persistToSupabase, t]);

  // ---------- drawing ----------
  const clearCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    setLocalSrc("");
    onChange?.("");
    setOk("");
    setErr("");
  }, [onChange]);

  const pointFromEvent = React.useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0, pressure: 0.5 };

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const p = typeof e.pressure === "number" ? e.pressure : 0.5;
    const pressure = clamp(p || 0.5, 0.15, 1);
    return { x, y, pressure };
  }, []);

  const onPointerDown = React.useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.setPointerCapture?.(e.pointerId);
      drawingRef.current.drawing = true;

      const { x, y, pressure } = pointFromEvent(e);
      drawingRef.current.lastX = x;
      drawingRef.current.lastY = y;

      const ctx = canvas.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(x, y);

      const w = 2 + pressure * 2;
      ctx.lineWidth = w;
      ctx.lineTo(x + 0.01, y + 0.01);
      ctx.stroke();

      setHasInk(true);
      setLocalSrc(""); // drawing overrides uploaded image
      setOk("");
      setErr("");
    },
    [pointFromEvent]
  );

  const onPointerMove = React.useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (!drawingRef.current.drawing) return;

      const ctx = canvas.getContext("2d");
      const { x, y, pressure } = pointFromEvent(e);

      const w = 2 + pressure * 2;
      ctx.lineWidth = w;

      ctx.beginPath();
      ctx.moveTo(drawingRef.current.lastX, drawingRef.current.lastY);
      ctx.lineTo(x, y);
      ctx.stroke();

      drawingRef.current.lastX = x;
      drawingRef.current.lastY = y;
    },
    [pointFromEvent]
  );

  const endStroke = React.useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!drawingRef.current.drawing) return;

    drawingRef.current.drawing = false;
    try {
      canvas.releasePointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
  }, []);

  // ---------- upload/camera pick ----------
  const readFileAsDataUrl = React.useCallback(async (file) => {
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = () =>
        reject(
          new Error(
            t("common.signature.readFailed", "Failed to read image."),
          ),
        );
      r.readAsDataURL(file);
    });
  }, [t]);

  const onPickedFile = React.useCallback(
    async (file) => {
      if (!file) return;
      setErr("");
      setOk("");
      const src = await readFileAsDataUrl(file);
      setLocalSrc(src);
      setHasInk(false);
      onChange?.(src);
    },
    [onChange, readFileAsDataUrl]
  );

  // ---------- save ----------
  const saveSignature = React.useCallback(async () => {
    setSaving(true);
    setErr("");
    setOk("");

    try {
      // If not persisting, just acknowledge and exit (caller stores it)
      if (!persistToSupabase) {
        const canvas = canvasRef.current;

        let nextSrc = "";
        if (localSrc) {
          nextSrc = localSrc;
        } else if (hasInk && canvas) {
          nextSrc = canvas.toDataURL(SIGNATURE_MIME, 1);
        } else if (value && String(value).startsWith("data:image")) {
          nextSrc = String(value);
        } else if (effectiveSrc) {
          nextSrc = String(effectiveSrc);
        }

        if (!nextSrc) {
          throw new Error(
            t("common.signature.addFirst", "Please add your signature first."),
          );
        }
        onChange?.(nextSrc);
        onSaved?.({ persisted: false, path: null, src: nextSrc });
        setOk(t("common.signature.saved", "Signature saved."));
        return;
      }

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        throw new Error(
          t("common.signature.signInToSave", "Please sign in to save your signature."),
        );
      }
      setUserId(user.id);

      const canvas = canvasRef.current;

      let pngBlob;
      if (localSrc) {
        pngBlob = dataUrlToBlob(localSrc);
      } else if (hasInk && canvas) {
        pngBlob = await blobFromCanvas(canvas);
      } else if (value && String(value).startsWith("data:image")) {
        pngBlob = dataUrlToBlob(value);
      } else {
        throw new Error(
          t("common.signature.addFirst", "Please add your signature first."),
        );
      }

      const safeUserId = String(user.id || "").toLowerCase();
      const candidatePaths = [
        // Primary expected by doctor_profiles_signature_path_check
        `doctors/${safeUserId}/signature.png`,
        // Backward-compatible fallback in case env has a stricter extension rule
        `doctors/${safeUserId}/signature.jpg`,
      ];

      let path = "";
      let lastDbErr = null;
      for (const candidatePath of candidatePaths) {
        const { error: upErr } = await supabase.storage
          .from(SIGNATURE_BUCKET)
          .upload(candidatePath, pngBlob, {
            contentType: SIGNATURE_MIME,
            upsert: true,
            cacheControl: "3600",
          });
        if (upErr) throw upErr;

        const { error: dbErr } = await supabase
          .from("doctor_profiles")
          .upsert(
            {
              user_id: user.id,
              signature_path: candidatePath,
              signature_mime: SIGNATURE_MIME,
              signature_updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" }
          );

        if (!dbErr) {
          path = candidatePath;
          lastDbErr = null;
          break;
        }

        lastDbErr = dbErr;
      }

      if (lastDbErr) {
        throw new Error(
          `${lastDbErr?.message || t("common.signature.saveFailed", "Failed to save signature.")} (path tried: ${candidatePaths.join(
            ", "
          )})`
        );
      }

      const { data: signed, error: sErr } = await supabase.storage
        .from(SIGNATURE_BUCKET)
        .createSignedUrl(path, 60 * 10);

      let nextPreviewUrl = "";
      if (!sErr && signed?.signedUrl) {
        nextPreviewUrl = signed.signedUrl;
      } else {
        const { data: pub } = supabase.storage.from(SIGNATURE_BUCKET).getPublicUrl(path);
        nextPreviewUrl = pub?.publicUrl || "";
      }

      if (nextPreviewUrl) {
        setSigPreviewUrl(nextPreviewUrl);
        onChange?.(nextPreviewUrl);
      }
      setSigPath(path);
      onSaved?.({ persisted: true, path, src: nextPreviewUrl || "" });

      setOk(t("common.signature.saved", "Signature saved."));
    } catch (e) {
      setErr(
        e?.message ||
          t("common.signature.saveFailed", "Failed to save signature."),
      );
    } finally {
      setSaving(false);
    }
  }, [persistToSupabase, effectiveSrc, hasInk, localSrc, onChange, onSaved, t, value]);

  const Surface = embedded ? "div" : Card;

  return (
    <Surface className="overflow-hidden">
      {showHeader ? (
        <div className="px-4 pt-4 pb-3">
          <div className="text-title-medium text-on-surface">{resolvedTitle}</div>
          {!!resolvedSubtitle ? <div className="mt-1 text-body-small text-on-surface-var">{resolvedSubtitle}</div> : null}
        </div>
      ) : null}

      <div className="px-4 pb-4">
        {loading ? <div className="text-body-medium text-on-surface-var">{t("common.loading", "Loading...")}</div> : null}
        {err ? <div className="text-body-medium text-[rgb(var(--clr-error))]">{err}</div> : null}
        {ok ? <div className="mt-2 text-body-medium text-[rgb(var(--clr-primary))]">{ok}</div> : null}

        {!sessionState.ok ? (
          <div className="mt-2 text-body-small text-on-surface-var">{sessionState.message}</div>
        ) : null}

        {/* Existing preview */}
        {!hidePreview && effectiveSrc ? (
          <div className="mt-4">
            <div className="text-label-medium text-on-surface-var">
              {t("common.signature.current", "Current signature")}
            </div>
            <div
              className={[
                "mt-2 rounded-[12px] p-3",
                embedded
                  ? "bg-[rgba(var(--clr-on-surface),0.05)]"
                  : "bg-[rgba(2,6,23,0.04)]",
              ].join(" ")}
            >
              <img src={effectiveSrc} alt={t("common.signature.alt", "Doctor signature")} className="h-14 w-auto" style={{ imageRendering: "auto" }} />
              {persistToSupabase ? (
                <div className="mt-2 text-label-small text-on-surface-var">
                  {t("common.signature.storedAt", "Stored at")}: <span className="font-mono">{sigPath || "—"}</span>
                </div>
              ) : null}
            </div>
          </div>
        ) : !hidePreview ? (
          <div className="mt-4 text-body-small text-on-surface-var">
            {t("common.signature.none", "No signature saved yet.")}
          </div>
        ) : null}

        {/* Pad */}
        <div className="mt-4">
          <div className="text-label-medium text-on-surface-var">
            {t("common.signature.draw", "Draw your signature")}
          </div>

          <div
            ref={wrapRef}
            className={[
              "mt-2 rounded-[12px] overflow-hidden",
              embedded
                ? "bg-[rgba(var(--clr-on-surface),0.04)] shadow-[inset_0_0_0_1px_rgba(var(--clr-outline-variant),0.55)]"
                : "border border-[rgba(2,6,23,0.10)] bg-[rgb(var(--clr-surface))]",
            ].join(" ")}
          >
            <canvas
              ref={canvasRef}
              className="block touch-none"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endStroke}
              onPointerCancel={endStroke}
              onPointerLeave={endStroke}
            />
          </div>

          {/* Actions */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={[
                  "h-10 px-3 rounded-[12px] text-body-medium text-on-surface",
                  embedded ? "bg-[rgba(var(--clr-on-surface),0.08)]" : "bg-[rgba(2,6,23,0.06)]",
                ].join(" ")}
                onClick={clearCanvas}
              >
                {t("common.clear", "Clear")}
              </button>

              <button
                type="button"
                className={[
                  "h-10 px-3 rounded-[12px] text-body-medium text-on-surface",
                  embedded ? "bg-[rgba(var(--clr-on-surface),0.08)]" : "bg-[rgba(2,6,23,0.06)]",
                ].join(" ")}
                onClick={() => uploadInputRef.current?.click?.()}
              >
                {t("common.signature.upload", "Upload")}
              </button>

              <button
                type="button"
                className={[
                  "h-10 px-3 rounded-[12px] text-body-medium text-on-surface",
                  embedded ? "bg-[rgba(var(--clr-on-surface),0.08)]" : "bg-[rgba(2,6,23,0.06)]",
                ].join(" ")}
                onClick={() => cameraInputRef.current?.click?.()}
              >
                {t("common.signature.camera", "Camera")}
              </button>
            </div>

            <SaveChip disabled={disabled} onClick={saveSignature}>
              {saving ? t("common.status.saving", "Saving...") : t("common.signature.save", "Save signature")}
            </SaveChip>
          </div>

          <div className="mt-3 text-label-small text-on-surface-var">
            {t(
              "common.signature.tip",
              "Tip: a stylus or rubber-tip pen usually works better than a finger. Pressure is supported when the device provides it.",
            )}
          </div>

          {/* hidden inputs */}
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              try {
                await onPickedFile(f);
              } catch (ex) {
                setErr(
                  ex?.message ||
                    t("common.signature.imageLoadFailed", "Failed to load image."),
                );
              }
            }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              try {
                await onPickedFile(f);
              } catch (ex) {
                setErr(
                  ex?.message ||
                    t("common.signature.imageLoadFailed", "Failed to load image."),
                );
              }
            }}
          />
        </div>
      </div>
    </Surface>
  );
}
